import React from 'react';
import FormattedNumber from 'components/FormattedNumber';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import { FixedCostValues } from '../types';
import {
  InfoRow,
  usePlanTabContentStyle,
  perUnitTotal,
  PlanDetails
} from './PlanDetailPanel.helpers';

const FIXED_COST_FIELDS: Array<{
  label: string;
  key: keyof FixedCostValues;
}> = [
  { label: 'Land Rent', key: 'landRent' },
  { label: 'Equipment', key: 'equipment' },
  { label: 'Labor', key: 'labor' },
  { label: 'Repairs', key: 'repairs' },
  { label: 'Taxes', key: 'taxes' },
  { label: 'Insurance', key: 'insurance' },
  { label: 'Return to Management', key: 'returnToMgt' }
];

export interface PlanFixedCostTabProps {
  plan: PlanDetails;
  displayByFinancialAccess: boolean;
}

/** The Plans detail panel's "Fixed Cost" tab. */
const PlanFixedCostTab: React.FC<PlanFixedCostTabProps> = ({
  plan,
  displayByFinancialAccess
}) => {
  const tabContentStyle = usePlanTabContentStyle();

  // Same left/right split Applications/Work Orders' own DetailsTab uses
  // (details-field-list/-columns-row/-left-col/-right-col, DetailsTab.css) —
  // two columns above 1440px, collapsing to a single flowing list below it.
  const leftFixedCostFields = FIXED_COST_FIELDS.filter((_, i) => i % 2 === 0);
  const rightFixedCostFields = FIXED_COST_FIELDS.filter((_, i) => i % 2 === 1);

  const renderFixedCostField = ({
    label,
    key
  }: {
    label: string;
    key: keyof FixedCostValues;
  }) => (
    <InfoRow key={key} label={label} labelStyle={{ minWidth: 180 }}>
      {displayByFinancialAccess && (
        <>
          <div>
            <FormattedNumber unit="usCurrency" roundedTo={2}>
              {perUnitTotal(plan?.fixedCost?.[key], plan?.estimatedAreaValue)}
            </FormattedNumber>
          </div>
          <div className="text-xs text-neutral-60">
            <FormattedNumber unit="usCurrency" roundedTo={2}>
              {plan?.fixedCost?.[key] ?? 0}
            </FormattedNumber>
            /{getAbbreviatedUnit(plan?.estimatedAreaUnit)}
          </div>
        </>
      )}
    </InfoRow>
  );

  return (
    <div className="details-tab-container" style={tabContentStyle}>
      <div
        className="px-6 py-8 min-w-0"
        data-testid="plan-detail-tab-panel-fixed-cost"
        style={{ paddingBottom: 72 }}
      >
        <div className="details-field-list">
          <div className="details-columns-row">
            <div className="details-left-col">
              {leftFixedCostFields.map(renderFixedCostField)}
            </div>
            <div className="details-right-col">
              {rightFixedCostFields.map(renderFixedCostField)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanFixedCostTab;
